/**
 * AnalyticsTab Component
 *
 * Displays batch analytics with visualizations:
 * - Jobs by state pie chart
 * - Completion timeline line chart
 * - Parse and run success rates
 * - Top errors list
 * - Uses Recharts library for charts
 */

import React from 'react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useBatchAnalytics } from '../../hooks/useBatchAnalytics';

interface AnalyticsTabProps {
  batchId: string;
}

// Colors for job state pie chart
const STATE_COLORS: Record<string, string> = {
  raw: '#F59E0B', // amber
  parsing: '#3B82F6', // blue
  parsed: '#10B981', // green
  parse_failed: '#EF4444', // red
  running: '#8B5CF6', // purple
  completed: '#059669', // emerald
  run_failed: '#DC2626', // dark red
};

// Type for pie chart label params
interface PieLabelParams {
  name: string;
  percent: number;
  value: number;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ batchId }) => {
  const { data, isLoading, error, refetch } = useBatchAnalytics(batchId);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading analytics</h3>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
            <div className="mt-4">
              <button
                onClick={() => refetch()}
                className="text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-label="Loading analytics"></div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Transform data for charts
  const jobsByStateData = Object.entries(data.jobs_by_state)
    .filter(([_, count]) => count > 0)
    .map(([state, count]) => ({
      name: state.replace('_', ' '),
      value: count,
      color: STATE_COLORS[state] || '#6B7280',
    }));

  const timelineData = data.completion_timeline.map((point) => ({
    time: new Date(point.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    count: point.count,
  }));

  // Custom label renderer with proper typing
  const renderCustomLabel = (params: any): string => {
    const { name, percent } = params as PieLabelParams;
    return `${name}: ${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Success Rate Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Parse Success Rate */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Parse Success Rate</h3>
          <div className="flex items-baseline">
            <p className="text-3xl font-semibold text-gray-900">{(data.parse_success_rate * 100).toFixed(1)}%</p>
            <p className="ml-2 text-sm text-gray-500">of jobs parsed successfully</p>
          </div>
        </div>

        {/* Run Success Rate */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Run Success Rate</h3>
          <div className="flex items-baseline">
            <p className="text-3xl font-semibold text-gray-900">{(data.run_success_rate * 100).toFixed(1)}%</p>
            <p className="ml-2 text-sm text-gray-500">of jobs completed successfully</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Jobs by State Pie Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Jobs by State</h3>
          {jobsByStateData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={jobsByStateData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {jobsByStateData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">No data available</div>
          )}
        </div>

        {/* Completion Timeline Line Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Completion Timeline</h3>
          {timelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} name="Jobs Completed" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">No timeline data available</div>
          )}
        </div>
      </div>

      {/* Execution Time Distribution */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Execution Time Distribution</h3>
        {Object.keys(data.execution_time_distribution).length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.execution_time_distribution).map(([range, count]) => (
              <div key={range} className="border border-gray-200 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500 mb-1">{range}</dt>
                <dd className="text-2xl font-semibold text-gray-900">{count}</dd>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No execution data available</div>
        )}
      </div>

      {/* Top Errors */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Top Errors</h3>
        {data.top_errors.length > 0 ? (
          <div className="space-y-3">
            {data.top_errors.map((errorItem, index) => (
              <div key={index} className="flex items-center justify-between border-b border-gray-200 pb-3 last:border-0">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{errorItem.error}</p>
                </div>
                <div className="ml-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {errorItem.count} occurrences
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No errors recorded</div>
        )}
      </div>
    </div>
  );
};
